import { useEffect, useRef, useState, type VideoHTMLAttributes } from 'react';
import { cameraTrack, listTracks, VideoPull, type GetVideoAuth } from './pull';

export type RobotVideoStatus = 'idle' | 'connecting' | 'live' | 'retrying';

export interface RobotVideoState {
  stream: MediaStream | null;
  status: RobotVideoStatus;
  error: string | null;
}

const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 15_000;

/**
 * Pulls one track and keeps it pulled: a lost session or a failed pull is
 * retried with backoff until the component unmounts or `track` changes.
 * `track` null pulls nothing.
 */
export function useRobotVideo(getAuth: GetVideoAuth, robotId: string | null, track: string | null): RobotVideoState {
  const [state, setState] = useState<RobotVideoState>({ stream: null, status: 'idle', error: null });
  // A new getAuth identity each render must not restart the stream.
  const authRef = useRef(getAuth);
  authRef.current = getAuth;

  useEffect(() => {
    if (!robotId || !track) {
      setState({ stream: null, status: 'idle', error: null });
      return;
    }
    const abort = new AbortController();
    let pull: VideoPull | null = null;
    (async () => {
      let backoff = RETRY_MIN_MS;
      while (!abort.signal.aborted) {
        setState((s) => ({ ...s, status: s.error ? 'retrying' : 'connecting' }));
        try {
          pull = await VideoPull.open((id) => authRef.current(id), robotId, [track], {
            jitterBuffer: false,
            signal: abort.signal,
            onTrack: () => {
              const stream = pull?.streams.get(track) ?? null;
              setState({ stream, status: 'live', error: null });
            },
          });
          const stream = pull.streams.get(track);
          if (stream) setState({ stream, status: 'live', error: null });
          backoff = RETRY_MIN_MS;
          const reason = await pull.ended;
          if (abort.signal.aborted) return;
          setState({ stream: null, status: 'retrying', error: reason });
        } catch (e) {
          if (abort.signal.aborted) return;
          setState({ stream: null, status: 'retrying', error: e instanceof Error ? e.message : String(e) });
        }
        await new Promise((r) => setTimeout(r, backoff));
        backoff = Math.min(backoff * 2, RETRY_MAX_MS);
      }
    })();
    return () => {
      abort.abort();
      pull?.close();
    };
  }, [robotId, track]);

  return state;
}

/**
 * The track that shows `camera` on `robotId` (see `cameraTrack`), looked up
 * once per robot/camera and again while the robot is not publishing it.
 */
export function useCameraTrack(getAuth: GetVideoAuth, robotId: string | null, camera: string | null): string | null {
  const [track, setTrack] = useState<string | null>(null);
  const authRef = useRef(getAuth);
  authRef.current = getAuth;
  useEffect(() => {
    setTrack(null);
    if (!robotId || !camera) return;
    const abort = new AbortController();
    (async () => {
      while (!abort.signal.aborted) {
        try {
          const found = cameraTrack(await listTracks((id) => authRef.current(id), robotId, abort.signal), robotId, camera);
          if (found) {
            setTrack(found);
            return;
          }
        } catch (e) {
          if (abort.signal.aborted) return;
          console.warn('robot-video: track list failed', e);
        }
        await new Promise((r) => setTimeout(r, RETRY_MAX_MS));
      }
    })();
    return () => abort.abort();
  }, [robotId, camera]);
  return track;
}

export interface RobotVideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  getAuth: GetVideoAuth;
  robotId: string;
  /** An exact track name (e.g. `{robot}_third_person-stereo`), or use `camera`. */
  track?: string;
  /** A camera name from the obs-gen config; resolved to its track with `useCameraTrack`. */
  camera?: string;
  /** Called whenever the pull's status changes, for an overlay the caller draws. */
  onStatus?: (state: RobotVideoState) => void;
}

/** A muted, autoplaying `<video>` showing one robot camera track from the SFU. */
export function RobotVideo({ getAuth, robotId, track, camera, onStatus, ...videoProps }: RobotVideoProps) {
  const resolved = useCameraTrack(getAuth, robotId, track ? null : (camera ?? null));
  const state = useRobotVideo(getAuth, robotId, track ?? resolved);
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.srcObject !== state.stream) ref.current.srcObject = state.stream;
  }, [state.stream]);
  useEffect(() => onStatus?.(state), [state, onStatus]);
  return <video ref={ref} autoPlay muted playsInline {...videoProps} />;
}
